import { MatchingSummary } from '../../../interfaces/DataQcInterface';
import { getMatchingSummary } from '../../../services/dataQcSummaryService';
import useSchools from '../../hooks/useSchools';
import WithDataWrapper from '../../reusable/WithDataWrapper';
import MatchingStatusInfo from './MatchingStatusInfo';
import CustomDivider from '../../reusable/CustomDivider';
import CustomAlert from '../../reusable/CustomAlert';
import { useDataQcOutletContext } from '../../hooks/useDataQcOutletContext';
import { DATA_CATEGORY_LABELS, fileTypes } from '../../../constants';
import useUploadProgress from '../../hooks/useUploadProgress';
import useDistrictRequiresTestScores from '../../hooks/useDistrictRequiresTestScores';
import useExternalSchoolCheck from '../hooks/useExternalSchoolCheck';
import ExternalSchoolMessage from '../ExternalSchoolMessage';

import './MatchingContent.css';

interface MatchingContentProps {
  data: MatchingSummary;
}

const CATEGORY_SECTIONS = Object.freeze({
  ROSTER_AND_DEMOGRAPHICS: 'roster_and_demographics',
  ROSTER_AND_TRANSCRIPTS: 'roster_and_transcripts',
  ROSTER_AND_TEST_SCORES: 'roster_and_test_scores',
  COURSES_IN_TRANSCRIPTS_AND_CATALOGS: 'courses_in_transcripts_and_catalogs',
  CTE_COURSES_AND_CATALOG: 'cte_courses_and_catalog',
});

const COURSE_CATEGORIES: Set<keyof MatchingSummary> = new Set([
  CATEGORY_SECTIONS.COURSES_IN_TRANSCRIPTS_AND_CATALOGS,
  CATEGORY_SECTIONS.CTE_COURSES_AND_CATALOG,
]);

const descriptions: { [key: string]: JSX.Element } = {
  students_matched_gender_percentage: (
    <span>
      matched with <span className="bolded">gender</span>
    </span>
  ),
  students_matched_race_ethnicity_percentage: (
    <span>
      matched with <span className="bolded">race/ethnicity</span>
    </span>
  ),
  students_matched_frl_status_percentage: (
    <span>
      matched with <span className="bolded">socioeconomic status</span>
    </span>
  ),
  students_matched_ell_status_percentage: (
    <span>
      matched with <span className="bolded">ELL status</span>
    </span>
  ),
  students_matched_sped_status_percentage: (
    <span>
      matched with <span className="bolded">disability status</span>
    </span>
  ),
  students_matched_transcripts_status_percentage: (
    <span>
      matched with <span className="bolded">transcripts</span>
    </span>
  ),
  students_matched_tests_sat_status_percentage: (
    <span>
      matched with an <span className="bolded">SAT test score</span>
    </span>
  ),
  students_matched_tests_act_status_percentage: (
    <span>
      matched with an <span className="bolded">ACT test score</span>
    </span>
  ),
  transcript_courses_matched_percentage: <span>matched</span>,
  cte_courses_matched_percentage: <span>matched</span>,
};

const sectionTitles: { [key: string]: string } = {
  roster_and_demographics: 'Roster and demographics data',
  roster_and_transcripts: 'Roster and transcript data',
  roster_and_test_scores: 'Roster and test score data',
  courses_in_transcripts_and_catalogs: 'Courses in transcripts and course catalog',
  cte_courses_and_catalog: 'CTE courses and course catalog data',
};

function MatchingContent({ data }: MatchingContentProps) {
  const { uploadProgress, dataQcSchool, schools } = useDataQcOutletContext();
  const { schoolsUploadProgressByCategory } = useUploadProgress();
  const { requiresTestScores } = useDistrictRequiresTestScores();
  const shouldShowExternalMessage = useExternalSchoolCheck();

  if (shouldShowExternalMessage) {
    return <ExternalSchoolMessage message="We do not collect matching data for external schools." />;
  }

  const getUploadStatus = () => {
    // SKIP UPLOADED DATA CHECKS - TO DELETE
    let skip = true;
    if (skip) return true;

    if (!dataQcSchool) return uploadProgress.pending.length + uploadProgress.progress.length === 0;

    const schoolData = schools.find((school) => school.id === dataQcSchool);
    if (!schoolData) return false;

    let schoolsProgresses = Object.values(schoolsUploadProgressByCategory);
    return schoolsProgresses.every((schoolProgress) => schoolProgress[schoolData.name] === 'completed');
  };

  const isUploadCompleted = getUploadStatus();

  const checkForSufficientData = (categoryName: string) => {
    // SKIP UPLOADED DATA CHECKS - TO DELETE
    let skip = true;
    if (skip) return true;

    let sufficientData = false;
    let isRostersDataCompleted = false;
    let isDemographicsDataCompleted = false;
    let isTranscriptsDataCompleted = false;
    let isTestScoresDataCompleted = false;
    let isCourseCatalogDataCompleted = false;
    let isCTECoursesDataCompleted = false;

    if (!dataQcSchool) {
      isRostersDataCompleted = uploadProgress.completed.some(
        (category) => category.categoryName === DATA_CATEGORY_LABELS[fileTypes.STUDENT_ROSTER]
      );
      isDemographicsDataCompleted = uploadProgress.completed.some(
        (category) => category.categoryName === DATA_CATEGORY_LABELS[fileTypes.STUDENT_DEMOGRAPHICS]
      );
      isTranscriptsDataCompleted = uploadProgress.completed.some(
        (category) => category.categoryName === DATA_CATEGORY_LABELS[fileTypes.STUDENT_TRANSCRIPTS]
      );
      isTestScoresDataCompleted = uploadProgress.completed.some(
        (category) => category.categoryName === DATA_CATEGORY_LABELS[fileTypes.TEST_SCORES]
      );
      isCourseCatalogDataCompleted = uploadProgress.completed.some(
        (category) => category.categoryName === DATA_CATEGORY_LABELS[fileTypes.COURSE_CATALOG]
      );
      isCTECoursesDataCompleted = uploadProgress.completed.some(
        (category) => category.categoryName === DATA_CATEGORY_LABELS[fileTypes.CTE_PATHWAYS]
      );
    } else {
      const schoolData = schools.find((school) => school.id === dataQcSchool);
      if (!schoolData) return false;

      let rostersUploadProgress = schoolsUploadProgressByCategory[DATA_CATEGORY_LABELS[fileTypes.STUDENT_ROSTER]];
      isRostersDataCompleted = rostersUploadProgress[schoolData.name] === 'completed';

      let demographicsUploadProgress =
        schoolsUploadProgressByCategory[DATA_CATEGORY_LABELS[fileTypes.STUDENT_DEMOGRAPHICS]];
      isDemographicsDataCompleted = demographicsUploadProgress[schoolData.name] === 'completed';

      let transcriptsUploadProgress =
        schoolsUploadProgressByCategory[DATA_CATEGORY_LABELS[fileTypes.STUDENT_TRANSCRIPTS]];
      isTranscriptsDataCompleted = transcriptsUploadProgress[schoolData.name] === 'completed';

      let testScoresUploadProgress = schoolsUploadProgressByCategory[DATA_CATEGORY_LABELS[fileTypes.TEST_SCORES]];
      isTestScoresDataCompleted = testScoresUploadProgress[schoolData.name] === 'completed';

      let courseCatalogUploadProgress = schoolsUploadProgressByCategory[DATA_CATEGORY_LABELS[fileTypes.COURSE_CATALOG]];
      isCourseCatalogDataCompleted = courseCatalogUploadProgress[schoolData.name] === 'completed';

      let CTECoursesUploadProgress = schoolsUploadProgressByCategory[DATA_CATEGORY_LABELS[fileTypes.CTE_PATHWAYS]];
      isCTECoursesDataCompleted = CTECoursesUploadProgress[schoolData.name] === 'completed';
    }

    switch (categoryName) {
      case CATEGORY_SECTIONS.ROSTER_AND_DEMOGRAPHICS:
        sufficientData = isRostersDataCompleted && isDemographicsDataCompleted;
        break;
      case CATEGORY_SECTIONS.ROSTER_AND_TRANSCRIPTS:
        sufficientData = isRostersDataCompleted && isTranscriptsDataCompleted;
        break;
      case CATEGORY_SECTIONS.ROSTER_AND_TEST_SCORES:
        sufficientData = isRostersDataCompleted && isTestScoresDataCompleted;
        break;
      case CATEGORY_SECTIONS.COURSES_IN_TRANSCRIPTS_AND_CATALOGS:
        sufficientData = isTranscriptsDataCompleted && isCourseCatalogDataCompleted;
        break;
      case CATEGORY_SECTIONS.CTE_COURSES_AND_CATALOG:
        sufficientData = isCTECoursesDataCompleted && isCourseCatalogDataCompleted;
        break;
    }

    return sufficientData;
  };

  return (
    <div className="matching-content" data-testid="matching-content">
      {!isUploadCompleted && (
        <div className="alert-container">
          <CustomAlert alertType="warning" header="Some school data is missing">
            <p className="no-margin">
              Data matching is based on the data uploaded so far. Please upload data for all{' '}
              {dataQcSchool ? 'categoeris' : 'schools'} to receive full matching information.
            </p>
          </CustomAlert>
        </div>
      )}
      {(Object.keys(data) as Array<keyof MatchingSummary>).map((categoryDataName, categoryIndex) => {
        if (!requiresTestScores && categoryDataName === CATEGORY_SECTIONS.ROSTER_AND_TEST_SCORES) return null;

        const isDataUploadedForCategory = checkForSufficientData(categoryDataName);
        if (!isDataUploadedForCategory) return null;

        return (
          <div className="matching-content-section" key={categoryDataName}>
            <p className="matching-content-section-title">{sectionTitles[categoryDataName]}</p>
            {Object.entries(data[categoryDataName]).map(([subSectionKey, subSectionValue]) => {
              const isCourseCategory = COURSE_CATEGORIES.has(categoryDataName);

              return (
                <MatchingStatusInfo
                  key={subSectionKey}
                  subjectType={isCourseCategory ? 'courses' : 'students'}
                  value={subSectionValue ?? 'N/A'}
                  description={descriptions[subSectionKey]}
                  className="alignment-fix"
                />
              );
            })}
            <CustomDivider orientation="horizontal" />
          </div>
        );
      })}
    </div>
  );
}

const MatchingContentWithData = () => {
  const { dataQcSchool } = useSchools();
  const shouldShowExternalMessage = useExternalSchoolCheck();

  if (shouldShowExternalMessage) {
    return <ExternalSchoolMessage message="We do not collect matching data for external schools." />;
  }

  let fetchData = (eoaId: string, access_token: string) => getMatchingSummary(eoaId, access_token, dataQcSchool);

  return (
    <WithDataWrapper
      fetchData={fetchData}
      errorMessage="Error occurred while fetching matching summary data!"
      initialState={{
        roster_and_demographics: {
          students_matched_gender_percentage: 0,
          students_matched_race_ethnicity_percentage: 0,
          students_matched_frl_status_percentage: 0,
          students_matched_ell_status_percentage: 0,
          students_matched_sped_status_percentage: 0,
        },
        roster_and_transcripts: {
          students_matched_transcripts_status_percentage: 0,
        },
        roster_and_test_scores: {
          students_matched_tests_sat_status_percentage: 0,
          students_matched_tests_act_status_percentage: 0,
        },
        courses_in_transcripts_and_catalogs: {
          transcript_courses_matched_percentage: 0,
        },
        cte_courses_and_catalog: {
          cte_courses_matched_percentage: 0,
        },
      }}
      options={{
        dependencies: [dataQcSchool],
        areDependenciesRequired: false,
      }}
    >
      {(data) => <MatchingContent data={data} />}
    </WithDataWrapper>
  );
};

export default MatchingContentWithData;
